import api from './client';
import {Capability, V2BrowseResponse} from './v2';

export type PlayerDocumentCategoryV2 = 'external_medical' | 'external_scout';

export interface PlayerDocumentV2 {
  id: string;
  title: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  category: PlayerDocumentCategoryV2;
  uploader: {id: string; name: string};
  notes?: string;
  document_date: string | null;
  created_at: string;
  capabilities: Capability[];
}

export interface PlayerDocumentFacetsV2 {
  categories: Array<{value: string; count: number}>;
}

export interface PlayerDocumentUploadV2 {
  file: File;
  title: string;
  category: PlayerDocumentCategoryV2;
  notes?: string;
  documentDate?: string;
}

export interface PlayerDocumentBytesV2 {
  blob: Blob;
  fileName?: string;
}

export const playerDocumentAccept = [
  '.pdf', '.doc', '.docx', 'application/pdf', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
].join(',');

const uploadTypes: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

const contentDispositionFileName = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const encoded = value.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try { return decodeURIComponent(encoded); } catch { return undefined; }
  }
  return value.match(/filename="([^"]+)"/i)?.[1] ?? value.match(/filename=([^;]+)/i)?.[1]?.trim();
};

const workspaceHeaders = (workspaceId?: string) => workspaceId ? {'X-Workspace-ID': workspaceId} : undefined;
const documentBytes = async (documentId: string, action: 'view' | 'download', workspaceId?: string): Promise<PlayerDocumentBytesV2> => {
  const response = await api.get(`/v2/player-documents/${documentId}/${action}`, {responseType: 'blob', headers: workspaceHeaders(workspaceId)});
  return {
    blob: response.data,
    fileName: contentDispositionFileName(response.headers?.['content-disposition']),
  };
};

export const playerDocumentsV2API = {
  list: async (
    playerId: string,
    pagination: {page?: number; pageSize?: number} = {}, workspaceId?: string,
  ): Promise<V2BrowseResponse<PlayerDocumentV2, PlayerDocumentFacetsV2>> => {
    const params = new URLSearchParams();
    if (pagination.page !== undefined) params.set('page', String(pagination.page));
    if (pagination.pageSize !== undefined) params.set('page_size', String(pagination.pageSize));
    const query = params.toString();
    const response = await api.get(`/v2/players/${playerId}/documents${query ? `?${query}` : ''}`, {headers: workspaceHeaders(workspaceId)});
    return response.data;
  },

  upload: async (playerId: string, upload: PlayerDocumentUploadV2, workspaceId?: string): Promise<{data: PlayerDocumentV2}> => {
    const form = new FormData();
    form.append('category', upload.category);
    form.append('title', upload.title);
    if (upload.notes) form.append('notes', upload.notes);
    if (upload.documentDate) form.append('document_date', upload.documentDate);
    form.append('file', upload.file);
    const response = await api.post(`/v2/players/${playerId}/documents`, form, {
      headers: {'Content-Type': 'multipart/form-data', ...workspaceHeaders(workspaceId)},
    });
    return response.data;
  },

  delete: async (documentId: string, workspaceId?: string): Promise<void> => {
    await api.delete(`/v2/player-documents/${documentId}`, {headers: workspaceHeaders(workspaceId)});
  },

  getViewPath: (documentId: string): string => `/v2/player-documents/${documentId}/view`,
  getDownloadPath: (documentId: string): string => `/v2/player-documents/${documentId}/download`,

  viewDocument: (documentId: string, workspaceId?: string): Promise<PlayerDocumentBytesV2> => documentBytes(documentId, 'view', workspaceId),
  downloadDocument: (documentId: string, workspaceId?: string): Promise<PlayerDocumentBytesV2> => documentBytes(documentId, 'download', workspaceId),

  validateUpload: (file: File): string | null => {
    if (file.size > 10 * 1024 * 1024) return 'Choose a file no larger than 10 MB.';
    const extension = file.name.match(/\.[^.]+$/)?.[0]?.toLowerCase() ?? '';
    const expectedType = uploadTypes[extension];
    if (!expectedType || (file.type !== '' && file.type !== expectedType)) {
      return 'Choose a PDF, DOC, or DOCX file whose type matches its extension.';
    }
    return null;
  },
};
